import fs from 'fs';
import path from 'path';
import { isNode } from '../helpers/isNode';
import { Parts } from './Parts';
import { Part } from './Part';
import { FileSystemNotFoundError, UnsupportedFileTypeError } from '../errors/errors';
export var UploadeMode;
(function (UploadeMode) {
    UploadeMode["Standard"] = "Standard";
    UploadeMode["FastUpload"] = "FastUpload";
})(UploadeMode || (UploadeMode = {}));
export class FileItem {
    constructor(fileItem) {
        this.partsToCreate = new Parts();
        this.partsToUpload = new Parts();
        this.partsToComplete = new Parts();
        this.completingParts = new Parts();
        this.inlinePartsToComplete = new Parts();
        this.partsCompleted = new Parts();
        const processedFile = this.processFileAttributes(fileItem);
        if ('file' in processedFile) {
            const { name, file } = processedFile;
            if (isNode() && typeof file === 'string') {
                this.name = name;
                try {
                    const { size } = fs.statSync(file);
                    this.size = size;
                }
                catch (error) {
                    throw new FileSystemNotFoundError(`ENOENT: No such file or directory for file : ${file}`);
                }
                this.originalFile = file;
            }
            else if (file instanceof File) {
                this.name = name;
                this.size = file.size;
                this.originalFile = file;
            }
            else {
                throw new UnsupportedFileTypeError('Unsupported file type.');
            }
        }
        else if ('content' in processedFile) {
            const { name, content } = processedFile;
            this.name = name;
            if (typeof content === 'string' || Buffer.isBuffer(content)) {
                this.size = content.length;
            }
            else {
                throw new UnsupportedFileTypeError('Unsupported file type.');
            }
            this.originalContent = content;
        }
        else {
            throw new UnsupportedFileTypeError('Unsupported file type.');
        }
    }
    isComplexFileWithContent(file) {
        if (file && typeof file === 'object' && 'name' in file && 'content' in file) {
            return true;
        }
        else {
            return false;
        }
    }
    isComplexFileWithPath(file) {
        if (file && typeof file === 'object' && 'name' in file && 'file' in file) {
            return true;
        }
        else {
            return false;
        }
    }
    isInlinedFileContentSupported(content) {
        return typeof content === 'string' || Buffer.isBuffer(content);
    }
    processFileAttributes(file) {
        if (this.isComplexFileWithContent(file)) {
            if (isNode() && file.content && this.isInlinedFileContentSupported(file.content)) {
                return { name: file.name, content: file.content };
            }
            else {
                throw new UnsupportedFileTypeError(`Unsupported file type for file ${file.name}`);
            }
        }
        else if (this.isComplexFileWithPath(file)) {
            if (isNode() && typeof file.file === 'string') {
                return { name: file.name, file: file.file };
            }
            else if (file.file instanceof File) {
                return { name: file.name, file: file.file };
            }
            else {
                throw new UnsupportedFileTypeError(`Unsupported file type for file ${file.name}`);
            }
        }
        else {
            if (isNode() && typeof file === 'string') {
                const name = path.basename(file);
                return { name, file };
            }
            else if (file instanceof File) {
                return { name: file.name, file };
            }
            else {
                throw new UnsupportedFileTypeError('Unsupported file type.');
            }
        }
    }
    populateCreatedFile({ file }) {
        this.id = file.id;
        this.region = file.region;
        this.transfer = file.transfer;
        this.chunckSize = file.chunkSize;
        this.partsCount = file.partsCount;
        this.uploadState = file.uploadState;
        this.maxInlineParts = file.maxInlineParts;
        this.initializePartsToCreate();
        if ('parts' in file && file.parts.length > 0) {
            this.initializePartsToUpload({ file });
        }
        return this;
    }
    initializePartsToCreate() {
        Array.from({ length: this.partsCount }, (_, i) => i + 1).map(index => this.partsToCreate.add(new Part({ id: index })));
    }
    initializePartsToUpload({ file: fileCreated }) {
        fileCreated.parts.forEach(part => {
            this.partsToUpload.add(new Part(part));
            this.partsToCreate.remove(part.id);
        });
    }
    populateCreatedParts(partsToCreate, { parts }) {
        const partsHydrated = new Parts();
        partsToCreate.forEach(part => {
            const partToCreate = this.partsToCreate.get(part.id);
            const createdPart = parts.find(partTofind => partTofind.id === part.id);
            if (createdPart) {
                partToCreate.hydrateCreatedPart(createdPart);
                partsHydrated.add(partToCreate);
            }
            this.partsToCreate.remove(partToCreate.id);
            this.partsToUpload.add(partToCreate);
        });
        return partsHydrated;
    }
    populateUploadingPart(partToUpload, { uploadedBytes }) {
        partToUpload.hydrateUploadingPart({ uploadedBytes });
        return partToUpload;
    }
    populateUploadedPart(partToUpload, { part }) {
        partToUpload.hydrateUploadedPart({ part });
        this.partsToUpload.remove(partToUpload.id);
        if (this.canAddInlinePart()) {
            this.inlinePartsToComplete.add(partToUpload);
        }
        else {
            this.partsToComplete.add(partToUpload);
        }
        return partToUpload;
    }
    getPartToComplete(part) {
        if (this.inlinePartsToComplete.has(part.id)) {
            const partToComplete = this.inlinePartsToComplete.get(part.id);
            this.inlinePartsToComplete.remove(partToComplete.id);
            return partToComplete;
        }
        else {
            const partToComplete = this.completingParts.get(part.id);
            this.completingParts.remove(partToComplete.id);
            return partToComplete;
        }
    }
    populateCompletedParts(partsToComplete, { parts }) {
        partsToComplete.forEach(part => {
            const partToComplete = this.getPartToComplete(part);
            const completedPart = parts.find(partTofind => partTofind.id === part.id);
            if (completedPart) {
                partToComplete.hydrateCompletedPart(completedPart);
            }
            this.partsCompleted.add(partToComplete);
        });
        return partsToComplete;
    }
    populateCompletedFile({ file }) {
        this.id = file.id;
        this.region = file.region;
        this.transfer = file.transfer;
        this.chunckSize = file.chunkSize;
        this.partsCount = file.partsCount;
        this.uploadState = file.uploadState;
        this.maxInlineParts = file.maxInlineParts;
        return this;
    }
    isFastUploadMode() {
        return this.uploadMode === UploadeMode.FastUpload;
    }
    isStandardUploadMode() {
        return this.uploadMode === UploadeMode.Standard;
    }
    getUploadMode() {
        return this.uploadMode;
    }
    get uploadMode() {
        if (this.partsCount > this.maxInlineParts) {
            return UploadeMode.Standard;
        }
        return UploadeMode.FastUpload;
    }
    canAddInlinePart() {
        return this.inlinePartsToComplete.length < this.maxInlineParts;
    }
    hasEnoughPartsUploadedToCompleteFile() {
        return this.partsToComplete.length + this.inlinePartsToComplete.length === this.partsCount;
    }
    hasEnoughPartsToValidate() {
        return this.partsToComplete.length >= this.maxInlineParts || (this.partsToComplete.length > 0 && this.partsCount - this.maxInlineParts - this.completingParts.length - this.partsCompleted.length === this.partsToComplete.length);
    }
    getPartsToValidate() {
        return this.partsToComplete;
    }
    getNextPartsToValidate() {
        const parts = this.partsToComplete.slice(0, this.maxInlineParts);
        parts.forEach(part => {
            this.partsToComplete.remove(part.id);
            this.completingParts.add(part);
        });
        return parts;
    }
    hasInlinePartsToValidate() {
        return Boolean(this.inlinePartsToComplete.list().length);
    }
    getInlinePartsToValidate() {
        return this.inlinePartsToComplete;
    }
    hasAllPartsUploaded() {
        return this.partsCompleted.length + this.inlinePartsToComplete.length === this.partsCount;
    }
    resetParts() {
        this.partsToCreate = new Parts();
        this.partsToUpload = new Parts();
        this.partsToComplete = new Parts();
        this.inlinePartsToComplete = new Parts();
        this.completingParts = new Parts();
        this.partsCompleted = new Parts();
        this.initializePartsToCreate();
    }
}
